import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DownloaderService } from '@core/downloader/downloader.service';
import { IssuingQuery } from '@core/issuing/issuing.query';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { Issuing } from '@core/models/issuing.model';
import { Product } from '@core/models/product.model';
import { OrganizationContextService } from '@core/context/organization-context.service';
import { ProductQuery } from '@core/product/product.query';
import { filterNil, toBoolean } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { DialogComponent, DialogData, DialogResult } from '@shared/dialog/dialog.component';
import { dataroomRoute, investorRoute, issuingProcessRoute, issuingRoute } from '@shared/shared-routes';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { mainParentId } from './dataroom-defaults';
import { MoveDialogComponent, MoveDialogData, MoveDialogResult } from './move-dialog/move-dialog.component';
import { NameDialogComponent, NameDialogData, NameDialogResult } from './name-dialog/name-dialog.component';
import { DataroomService } from '@core/dataroom/dataroom.service';
import { UserDataroomService } from '@core/dataroom/user-dataroom.service';
import { ClientDataroomService } from '@core/dataroom/client-dataroom.service';
import { PreviewDialogComponent, PreviewDialogData } from '@shared/dataroom/preview-dialog/preview-dialog.component';
import { NavigableNode } from '@shared/dataroom/dataroom.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  BulkDownloadBottomSheetComponent,
  BulkDownloadBottomSheetData,
} from '@shared/dataroom/bulk-download-bottom-sheet/bulk-download-bottom-sheet.component';

type DataroomScope = BehaviorSubject<NavigableNode>;

@Injectable({
  providedIn: 'root',
})
export class DataroomActionsService {
  private service: DataroomService | UserDataroomService | ClientDataroomService;
  public lastUploadedFile: File;
  // Prevent concurrent key metric confirmations while a request is in-flight
  private isKeyMetricMutationInFlight = false;

  setService(service: DataroomService | UserDataroomService | ClientDataroomService): void {
    this.service = service;
  }

  constructor(
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private downloader: DownloaderService,
    private issuingQuery: IssuingQuery,
    private organizationContextService: OrganizationContextService,
    private productQuery: ProductQuery,
    private translate: TranslateService,
  ) {}

  public download(node: DataroomNode): Observable<boolean> {
    return this.downloader.downloadAndSave(node.file.url, node.name);
  }

  public bulkDownload(nodes: DataroomNode[]) {
    this.bottomSheet.open<BulkDownloadBottomSheetComponent, BulkDownloadBottomSheetData>(
      BulkDownloadBottomSheetComponent,
      {
        data: {
          nodes,
        },
      },
    );
  }

  public preview(node: DataroomNode): void {
    this.dialog.open<PreviewDialogComponent, PreviewDialogData>(PreviewDialogComponent, {
      data: {
        node,
      },
      width: '75dvw',
      height: '90vh',
      maxWidth: '80vw',
      panelClass: 'preview-dialog-container',
    });
  }

  public async uploadDialog(
    scope: DataroomScope,
    onClose?: (response?: any) => void,
    isUpdate?: boolean,
    node?: DataroomNode,
  ): Promise<void> {
    // Dynamically import to avoid circular dependency
    const { UploadDialogComponent } = await import('./upload-dialog/upload-dialog.component');

    const dialogRef = this.dialog.open(UploadDialogComponent, {
      data: {
        activeScope: scope,
        onUploadComplete: onClose,
        isUpdate,
        node,
      },
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'upload-dialog-container',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      if (onClose) {
        onClose();
      }
    });
  }

  public rename(node: DataroomNode): Observable<DataroomNode> {
    return this.dialog
      .open<NameDialogComponent, NameDialogData, NameDialogResult>(NameDialogComponent, {
        data: {
          title: this.translate.instant('INTL_PRODUCT_DATAROOM_ACTION_RENAME'),
          name: node.name,
        },
      })
      .afterClosed()
      .pipe(
        filterNil,
        switchMap((result) => {
          const formData: FormData = new FormData();
          formData.append('name', result.name);
          return this.service.useResourceSuffix('nodes').update<DataroomNode>(node.id, formData, { skipWrite: true });
        }),
      );
  }

  public delete(node: DataroomNode): Observable<DataroomNode> {
    return this.dialog
      .open<DialogComponent, DialogData, DialogResult>(DialogComponent, {
        data: {
          title: this.translate.instant('INTL_DELETE'),
          text:
            node.type === 'file'
              ? this.translate.instant('INTL_DELETE_ITEM_TEXT', {
                  name: node.name,
                })
              : this.translate.instant('INTL_DELETE_ITEM_CONTENTS_TEXT', {
                  name: node.name,
                }),
          confirmButton: {
            icon: 'delete',
            label: this.translate.instant('INTL_DELETE'),
            iconPlacement: 'suffix',
          },
          dismissButton: {
            icon: 'arrow_back',
            label: this.translate.instant('INTL_CANCEL'),
          },
        },
      })
      .afterClosed()
      .pipe(
        filter((result) => toBoolean(result)),
        switchMap(() => {
          return this.service.useResourceSuffix('nodes').delete<DataroomNode>(node.id, { skipWrite: true });
        }),
      );
  }

  public changeSummaryStatus(node: DataroomNode): Observable<DataroomNode> {
    return this.dialog
      .open(DialogComponent, {
        data: {
          title:
            node.displayInProductSummary === false
              ? this.translate.instant('INTL_ADD_TO_PRODUCT_SUMMARY')
              : this.translate.instant('INTL_REMOVE_FROM_PRODUCT_SUMMARY'),
          text:
            node.displayInProductSummary === false
              ? this.translate.instant('INTL_ADD_IN_SUMMARY', {
                  name: node.name,
                })
              : this.translate.instant('INTL_REMOVE_FROM_SUMMARY', {
                  name: node.name,
                }),
          confirmButton: {
            icon: node.displayInProductSummary === false ? 'add' : 'remove',
            label:
              node.displayInProductSummary === false
                ? this.translate.instant('INTL_ADD')
                : this.translate.instant('INTL_REMOVE'),
            iconPlacement: 'suffix',
          },
          dismissButton: {
            icon: 'arrow_back',
            label: this.translate.instant('INTL_CANCEL'),
          },
        },
      })
      .afterClosed()
      .pipe(
        filterNil,
        switchMap((result) => {
          if (result) {
            const formData: FormData = new FormData();
            const newStatus = node.displayInProductSummary ? 'false' : 'true';
            formData.append('displayInProductSummary', newStatus);
            return this.service.useResourceSuffix('nodes').update<DataroomNode>(node.id, formData, { skipWrite: true });
          } else {
            return of(null);
          }
        }),
      );
  }

  public changeKeyMetricsStatus(node: DataroomNode): Observable<DataroomNode> {
    // No need to check for other key metrics - multiple files can be marked
    // Only disable button if mutation is in flight
    const confirmButtonDisabled = this.isKeyMetricMutationInFlight;

    return this.dialog
      .open<DialogComponent, DialogData, DialogResult>(DialogComponent, {
        data: {
          title: !node.keyMetric
            ? this.translate.instant('INTL_MARK_AS_KEY_METRICS')
            : this.translate.instant('INTL_UNMARK_AS_KEY_METRICS'),
          text: !node.keyMetric
            ? this.translate.instant('INTL_MARK_AS_KEY_METRICS_TEXT', { name: node.name })
            : this.translate.instant('INTL_UNMARK_AS_KEY_METRICS_TEXT', { name: node.name }),
          confirmButton: {
            icon: !node.keyMetric ? 'add' : 'remove',
            label: !node.keyMetric ? this.translate.instant('INTL_MARK') : this.translate.instant('INTL_UNMARK'),
            iconPlacement: 'suffix',
          },
          confirmButtonDisabled,
          dismissButton: { icon: 'arrow_back', label: this.translate.instant('INTL_CANCEL') },
        },
      })
      .afterClosed()
      .pipe(
        filterNil,
        switchMap((result) => {
          if (!result) {
            return of(null);
          }

          const formData: FormData = new FormData();
          const newStatus = node.keyMetric ? 'false' : 'true';
          formData.append('keyMetric', newStatus);

          this.isKeyMetricMutationInFlight = true;
          return this.service
            .useResourceSuffix('nodes')
            .update<DataroomNode>(node.id, formData, { skipWrite: true })
            .pipe(finalize(() => (this.isKeyMetricMutationInFlight = false)));
        }),
      );
  }

  public changeRegulatoryDocumentStatus(node: DataroomNode): Observable<DataroomNode> {
    return this.dialog
      .open(DialogComponent, {
        data: {
          title: !node.regulatoryRequiredDocument
            ? this.translate.instant('INTL_MARK_AS_REGULATORY')
            : this.translate.instant('INTL_UNMARK_AS_REGULATORY'),
          text: !node.regulatoryRequiredDocument
            ? this.translate.instant('INTL_MARK_AS_REGULATORY_TEXT', {
                name: node.name,
              })
            : this.translate.instant('INTL_UNMARK_AS_REGULATORY_TEXT', {
                name: node.name,
              }),
          confirmButton: {
            icon: !node.regulatoryRequiredDocument ? 'add' : 'remove',
            label: !node.regulatoryRequiredDocument
              ? this.translate.instant('INTL_MARK')
              : this.translate.instant('INTL_UNMARK'),
            iconPlacement: 'suffix',
          },
          dismissButton: {
            icon: 'arrow_back',
            label: this.translate.instant('INTL_CANCEL'),
          },
        },
      })
      .afterClosed()
      .pipe(
        filterNil,
        switchMap((result) => {
          if (result) {
            const formData: FormData = new FormData();
            const newStatus = node.regulatoryRequiredDocument ? 'false' : 'true';
            formData.append('regulatoryRequiredDocument', newStatus);
            return this.service.useResourceSuffix('nodes').update<DataroomNode>(node.id, formData, { skipWrite: true });
          } else {
            return of(null);
          }
        }),
      );
  }

  public upload(file: File, parentId?: string): Observable<FormData> {
    const formData: FormData = new FormData();
    const key: keyof DataroomNode = 'file';
    formData.append('type' as keyof DataroomNode, key);
    formData.append(key, file, file.name);
    formData.append('parent', parentId || mainParentId);
    this.service.setLoading(true);
    this.lastUploadedFile = file;

    return this.service
      .useUrlSuffix('nodes')
      .add<FormData>(formData)
      .pipe(
        take(1),
        catchError((error) => {
          this.service.setLoading(false);
          throw error;
        }),
      );
  }

  public updateFile(file: File, node: DataroomNode, parentId?: string) {
    const formData: FormData = new FormData();
    const key: keyof DataroomNode = 'file';
    formData.append('type' as keyof DataroomNode, key);
    formData.append(key, file, node.name);
    formData.append('parent', parentId || mainParentId);

    this.service.setLoading(true);

    return this.service
      .useResourceSuffix('nodes')
      .update<FormData>(node.id, formData)
      .pipe(
        take(1),
        catchError((error) => {
          this.service.setLoading(false);
          throw error;
        }),
      );
  }

  public createFolder(parentId?: string): Observable<DataroomNode> {
    return this.dialog
      .open<NameDialogComponent, NameDialogData, NameDialogResult>(NameDialogComponent, {
        data: {
          title: this.translate.instant('INTL_PRODUCT_DATAROOM_ACTION_NEW_FOLDER'),
          name: '',
        },
      })
      .afterClosed()
      .pipe(
        filterNil,
        switchMap((result) => {
          const formData: FormData = new FormData();
          formData.append('name', result.name);
          formData.append('type', 'folder');
          formData.append('file', '');
          formData.append('parent', parentId || mainParentId);
          return this.service.useResourceSuffix('nodes').add<DataroomNode>(formData, { skipWrite: true });
        }),
      );
  }

  public move(node: DataroomNode): Observable<DataroomNode> {
    return this.dialog
      .open<MoveDialogComponent, MoveDialogData, MoveDialogResult>(MoveDialogComponent, {
        data: {
          id: node.id,
          service: this.service,
        },
        width: '40vw',
      })
      .afterClosed()
      .pipe(
        filterNil,
        switchMap((result) => {
          const formData: FormData = new FormData();
          formData.append('parent', result.id || mainParentId);
          return this.service.useResourceSuffix('nodes').update<DataroomNode>(node.id, formData);
        }),
      );
  }

  public getNodesFolderAbsoluteRouterLink(node: DataroomNode): string[] {
    // Use context-aware organization type check
    const effectiveType = this.organizationContextService.getEffectiveOrganizationTypeSync();
    if (effectiveType === 'emittent') {
      const productId: Product['id'] = this.productQuery.getActiveId();
      return [issuingRoute, productId, dataroomRoute, node.parent?.id ?? ''];
    }

    const issuingId: Issuing['id'] = this.issuingQuery.getActiveId();
    return [
      investorRoute,
      issuingRoute,
      issuingId,
      issuingProcessRoute,
      issuingId,
      dataroomRoute,
      node.parent?.id ?? '',
    ];
  }
}
